/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {dispatchGridColumnsChange} from '@ion/applications/src/store/helpers';

export interface UseGridColumnPersistenceParams {
  name: string;
  action: string;
  columnState: any;
};

/**
 * Common hook used during Grid column changes to have update columnState value.
 *
 * ```typescript
 *
 *    import useGridColumnPersistence from '@ion/query/src/hooks/';
 *
 *    const onUpdateGridColumns = useGridColumnPersistence({
 *      name   : NAME,
 *      action : UPDATE_GRID_COLUMNS,
 *      columnState
 *    });
 *
 * ```
 */
export const useGridColumnPersistence =
  ({name, action, columnState}: UseGridColumnPersistenceParams): Function => {
    const [gridUpdateStatus, setGridUpdateStatus] = useState(false);
    const dispatch                                = useDispatch();

    useEffect(() => {
      if (columnState && gridUpdateStatus) {
        dispatch(dispatchGridColumnsChange(columnState, name, action));
        setGridUpdateStatus(false);
      }
    }, [columnState, gridUpdateStatus]);

    const onUpdateGridColumns = useCallback(() => {
      setGridUpdateStatus(true);
    }, []);

    return onUpdateGridColumns;
  };

export default useGridColumnPersistence;
